import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameActivity } from '../../enum/GameActivity';
import { GeoClass } from '../../interface/response/GeoFiltersResponse';
import { FilterType } from '../../enum/FilterType';

/**
 * Interface defining the shape of the global filters state.
 * Each property represents a filter category, which can hold any type of data.
 *
 * Initial state for the global filters.
 * - Most filters are initialized as empty arrays.
 * - GameActivity and TestData are initialized with default objects containing specific IDs.

 * Redux slice for managing global filters.
 * - Name: 'globalFilters'
 * - Initial state: `initialState`
 * 
 * Reducers:
 * - setGlobalFilter: Updates a specific filter key with a new value.
 *   - Payload: an object containing the key (one of GlobalFiltersState keys) and the new value.
 * - resetGlobalFilters: Resets the filters to the initial state.
 */

interface GlobalFiltersState {
  Client: GeoClass[] | null;
  Company: GeoClass[] | null;
  Region: GeoClass[] | null;
  Group: GeoClass[] | null;
  Location: GeoClass[] | null;
  Device: GeoClass[] | null;
  GameActivity: { id: GameActivity };
  PaymentSource: GeoClass[] | null;
  Games: GeoClass[] | null;
  TestData:
    | {
        id: true;
      }
    | any;
  filterType: FilterType;
  showColumnOrder: boolean;
}

const initialState: GlobalFiltersState = {
  Client: null,
  Company: null,
  Region: null,
  Group: null,
  Location: null,
  Device: null,
  GameActivity: {
    id: GameActivity.ALL,
  },
  PaymentSource: null,
  Games: null,
  TestData: {
    id: true,
  },
  filterType: FilterType.NORMAL,
  showColumnOrder: true,
};

const globalFiltersSlice = createSlice({
  name: 'globalFilters',
  initialState,
  reducers: {
    setGlobalFilter: (
      state,
      action: PayloadAction<{
        key: keyof GlobalFiltersState;
        value: GeoClass[] | { id: GameActivity | boolean } | null | FilterType | boolean | unknown;
      }>,
    ) => {
      state[action.payload.key] = action.payload.value;
    },
    resetGlobalFilters: (state) => {
      return {
        ...initialState,
        filterType: state.filterType,
        showColumnOrder: state.showColumnOrder,
      };
    },
    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },
    setShowColumnOrder(state, action: { payload: boolean }) {
      state.showColumnOrder = action.payload;
    },
  },
});

export const { setGlobalFilter, resetGlobalFilters, setFilterType, setShowColumnOrder } = globalFiltersSlice.actions;
export default globalFiltersSlice.reducer;
